import React, { useEffect, useMemo, useState } from 'react';
import { AddUniversal, WarningAltOutlined } from '@duxton-web/icons';
import {
  Button,
  Divider,
  Modal,
  Select,
  Spin,
  Tag,
  Tooltip,
} from '@duxton-web/ui';
import { useQueryClient } from '@tanstack/react-query';
import _, { debounce } from 'lodash';
import { useParams } from 'react-router-dom';

import {
  getVariables,
  isCreateMode,
  isEditMode,
  isViewMode,
  RestrictedBy,
} from '../../constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '../../contexts/experiment-context';
import { addExperimentVariable } from '../../store/actions/experiment/action-creators';
import { useUserRolesContext } from '@/contexts/UserRolesContext';
import { useFetchVariables } from '@/hooks';
import { Variable, VariableInfo } from '@/lib/PlannerDTOs';
import {
  buildAccessTargetFromVariable,
  UserAction,
} from '@/utils/accessControl';

const VariableTag = ({ item }: { item: VariableInfo }) => {
  let color = '';
  let label = '';
  switch (item.RestrictedBy) {
    case RestrictedBy.COUNTRY:
      color = 'gold';
      label = 'Country';
      break;
    case RestrictedBy.REGION:
      color = 'purple';
      label = 'Region';
      break;
    case RestrictedBy.MULTI:
      color = 'green';
      label = 'Multi';
      break;
    default:
      color = 'red';
      label = 'City';
      break;
  }

  return (
    <Tag size="xsmall" color={color}>
      {label}
    </Tag>
  );
};

const AddVariableModal = ({
  open,
  onCancel,
  onOk,
}: {
  open: boolean;
  onCancel: () => void;
  onOk: (variable: VariableInfo) => void;
}) => {
  const params = useParams<{ action: string }>();
  const action = params.action;

  const experimentContext = useExperimentContext();
  const { configurations, experiment } = experimentContext;
  const experimentDispatch = useExperimentDispatchContext();
  const experimentDomain = experiment?.domain;
  const variables = getVariables(configurations);
  const selectedVariablesName =
    variables?.map((variable: Variable) => variable.name) || [];

  const handleAddVariable = (variable: VariableInfo) => {
    experimentDispatch(addExperimentVariable(variable));
  };

  const [searchName, setSearchName] = useState('');
  const [selectedVariable, setSelectedVariable] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setSearchName('');
      setSelectedVariable(null);
    }
  }, [open]);

  const { data, isLoading } = useFetchVariables({
    name: searchName,
    domains: experimentDomain ? [experimentDomain] : null,
    domainRequired: false,
  });

  const { permissionUtils } = useUserRolesContext();

  const options = useMemo(() => {
    const actionRequired = (() => {
      if (isCreateMode(action)) {
        return UserAction.CREATE_EXPERIMENT_USING_CURRENT_VARIABLE;
      }

      if (isEditMode(action)) {
        return UserAction.EDIT_EXPERIMENT_USING_CURRENT_VARIABLE;
      }

      return null;
    })();

    if (data?.items?.length) {
      const isItemEnabled = (item) => {
        return (
          !selectedVariablesName.includes(item.name) &&
          !experimentDomain &&
          item?.domain !== experimentDomain &&
          actionRequired &&
          permissionUtils.canPerformAction(actionRequired, [item.techFamily], {
            accessTargets: [buildAccessTargetFromVariable(item)],
          })
        );
      };

      return _.sortBy(data.items, isItemEnabled).map((item) => {
        const [hasAccess, tooltipContent] = actionRequired
          ? permissionUtils.generateExplanation({
              action: actionRequired,
              techFamilies: [item.techFamily],
              accessTargets: [buildAccessTargetFromVariable(item)],
            })
          : [true, ''];

        return (
          <Select.Option
            disabled={!hasAccess}
            value={item.name}
            key={item.name}
          >
            <div>
              <div style={{ textAlign: 'left' }}>
                {!hasAccess && (
                  <Tooltip title={tooltipContent} placement="left">
                    <WarningAltOutlined
                      style={{ color: '#faad14', marginRight: 4 }}
                    />
                  </Tooltip>
                )}
                {item.name}
                <span style={{ float: 'right' }}>
                  <Tag size="xsmall" color="gray">
                    {item.type}
                  </Tag>
                  <VariableTag item={item} />
                  <Tag size="xsmall" color="blue">
                    {item.domain}
                  </Tag>
                </span>
              </div>
            </div>
          </Select.Option>
        );
      });
    }
    return [];
  }, [data]);

  const debounceSearchVariable = debounce((value) => {
    queryClient.invalidateQueries({
      queryKey: [
        'variables',
        searchName,
        experimentDomain ? [experimentDomain] : null,
      ],
    });
    setSearchName(value);
  }, 700);

  if (!open) return null;

  return (
    <Modal
      open
      centered
      title="Add Variable"
      onOk={() => {
        handleAddVariable(selectedVariable);

        onOk(null);
      }}
      onCancel={onCancel}
      okText="Add Variable"
      okButtonProps={{
        size: 'small',
        disabled:
          !selectedVariable ||
          selectedVariablesName.includes(selectedVariable?.name),
      }}
      cancelButtonProps={{ size: 'small' }}
      width={700}
      closable={false}
      maskClosable={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div>Variable Name</div>
        <Select
          showSearch
          loading={isLoading}
          filterOption={false}
          onSearch={(value) => {
            debounceSearchVariable(value);
          }}
          notFoundContent={
            isLoading ? (
              <Spin size="small" />
            ) : (
              <div>No matching variable found</div>
            )
          }
          dropdownRender={(menu) => (
            <>
              {menu}
              {data?.total && (
                <>
                  <Divider style={{ margin: '4px 0' }} />
                  <div
                    style={{
                      marginLeft: 12,
                      marginBottom: 4,
                      fontWeight: 'bold',
                    }}
                  >{`Showing ${options.length} of ${data.total} of matching variables`}</div>
                </>
              )}
            </>
          )}
          placeholder="Type to search variable"
          value={selectedVariable?.name}
          onChange={(value) => {
            setSelectedVariable(data.items.find((item) => item.name === value));
          }}
        >
          {options}
        </Select>
      </div>
    </Modal>
  );
};

const AddVariable = () => {
  const params = useParams<{ action: string }>();
  const action = params.action;
  const disabled = isViewMode(action);

  const [open, setOpen] = useState(false);

  if (disabled || isEditMode(action)) return null;

  return (
    <>
      <Button
        type="primary"
        size="small"
        icon={<AddUniversal />}
        style={{ width: '100%' }}
        onClick={() => setOpen(true)}
      >
        Add Variable
      </Button>
      <AddVariableModal
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
      />
    </>
  );
};

export default AddVariable;
